import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { UIAvatarSelectionItem } from "../common_ui_elements/ui_avatar_selection_item.js";
import { UIScrollableContainerCSS } from "../common_ui_elements/scrollable_container_css.js";
import { DialogManager } from "./dialog_manager.js";
import { setButtonTextOutline } from "../common_ui_elements/button_text_outline.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { ImgID } from "../../image_loader/img_id.js";
import { AvatarUtils } from "../../common/avatar_utils.js";
import { LeaderboardSyncService } from "../../api/leaderboard/leaderboard_sync_service.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { UITextElement } from "../common_ui_elements/ui_text_element.js";
import { MyTextAlign } from "../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { SaveDialog } from "./auth_dialogs/save_dialog.js";
import { updatePlayer } from "../../api/requests/player.requests.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";

export class AvatarSelectionDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.AVATAR_SELECTION_DIALOG;

  private static readonly AVATAR_KEY = 'player_avatar_id';

  private static readonly FACEBOOK_AVATAR_ENABLED = true;

  private last_avatar_id: number = 0;
  private selected_avatar_id: number = 0;
  private facebook_av_url: string | null = null;
  private confirm_button!: UIImage;
  private confirm_text?: UITextElement;

  private main_avatar!: UIAvatarSelectionItem;

  private avatars_scrollable_container!: UIScrollableContainerCSS;
  private avatar_items: UIAvatarSelectionItem[] = [];
  private grid_slots: { avatar_id: number; x: number; y: number; size: number }[] = [];
  private grid_touch_down: { x: number; y: number } | null = null;

  private readonly COLS = 4;

  private onConfirm: ( avatar_id: number ) => void;

  constructor( onConfirm: ( avatar_id: number ) => void )
  {
    const design_h = 1242;
    const design_w = 909;

    const h = LayoutScaler.heightFromDesign( design_h );
    const w = LayoutScaler.widthFromDesign( design_w );
    const x = ( DialogManager.getWidth() - w ) / 2;
    const y = ( DialogManager.getHeight() - h ) / 2;

    super( DialogManager.ctx, x, y, w, h );

    const saved = parseInt( localStorage.getItem( AvatarSelectionDialog.AVATAR_KEY ) ?? '0', 10 );
    const saved_avatar_id = isNaN( saved ) ? 0 : saved;
    this.selected_avatar_id = !AvatarSelectionDialog.FACEBOOK_AVATAR_ENABLED
      && saved_avatar_id === AvatarUtils.FACEBOOK_AVATAR_ID
        ? 0
        : saved_avatar_id;
    this.last_avatar_id = this.selected_avatar_id;
    this.facebook_av_url = localStorage.getItem( 'player_facebook_avatar_url' );

    this.drawBannerArea();
    this.createTitle();
    this.createCloseButton();
    this.createScrollableAvatarsGrid();
    this.createConfirmButton();

    this.onConfirm = onConfirm;
  }

  name(): string
  {
    return "AvatarSelectionDialog";
  }

  private createTitle(): void
  {
    const design_bg_size    = 287;
    const design_bg_y       = -149;
    const design_avatar_y   = 37;
    const design_avatar_size = 220;
    const design_border_w   = 5;

    const bg_size = LayoutScaler.widthFromDesign( design_bg_size );
    const bg_x    = this.rect.getW2() - bg_size / 2;
    const bg_y    = LayoutScaler.yFromDesign( design_bg_y );

    const bg = new UIImage(
      this.ctx,
      bg_x,
      bg_y,
      bg_size,
      bg_size,
      ImgID.AvatarCircle,
      {},
      false
    );

    this.addChild( bg );

    const avatar_size = LayoutScaler.widthFromDesign( design_avatar_size );
    const avatar_x    = bg_size / 2 - avatar_size / 2;
    const avatar_y    = LayoutScaler.yFromDesign( design_avatar_y );

    this.main_avatar = new UIAvatarSelectionItem(
      this.ctx,
      avatar_x,
      avatar_y,
      avatar_size,
      avatar_size,
      this.selected_avatar_id,
      () => {},
      LayoutScaler.widthFromDesign( design_border_w ),
      0,
      undefined,
      false,
      {
        type: "linear-gradient",
        from: "#F9F4C9",
        to: "#B5A084",
        direction: "vertical"
      },
      this.facebook_av_url
    );

    bg.addChild( this.main_avatar );
  }

  private createCloseButton(): void
  {
    const design_button_size = 117;
    const design_button_inset_x = 0;
    const design_button_y = -55;

    const button_size = LayoutScaler.widthFromDesign( design_button_size );
    const button_x = this.rect.getW() - button_size - LayoutScaler.widthFromDesign( design_button_inset_x );
    const button_y = LayoutScaler.yFromDesign( design_button_y );

    const button_bg = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_size,
      button_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    button_bg.setOnClick( () =>
    {
      this.close();
    } );

    this.addChild( button_bg );
  }

  private createScrollableAvatarsGrid(): void
  {
    const design_content_w    = 730;
    const design_content_h    = 848;
    const design_content_y    = 150;
    const design_gap          = 20;
    const design_border_overhang = 4;
    const design_gap_y        = 20;
    const design_item_size    = 164;
    const design_border_w     = 4;

    const content_w  = LayoutScaler.widthFromDesign( design_content_w );
    const content_h  = LayoutScaler.heightFromDesign( design_content_h );
    const content_x  = this.rect.getW2() - content_w / 2;
    const content_y  = LayoutScaler.yFromDesign( design_content_y );
    const gap        = LayoutScaler.heightFromDesign( design_gap );

    const content_bg = new RoundedRectangle(
      this.ctx,
      content_x,
      content_y,
      content_w,
      content_h,
      LayoutScaler.cornerRadiusFromDesign( 24 ),
      ColorScheme.settings_link_btn,
      undefined,
      false,
      undefined,
      0,
      {
        color: "rgba(255, 255, 255, 0.25)",
        offsetX: 0,
        offsetY: 0,
        blur: LayoutScaler.widthFromDesign( 6 ),
        sides: [ false, true, true, true ],
        sideOffsets: {
          right: LayoutScaler.widthFromDesign( 3 ),
          bottom: LayoutScaler.heightFromDesign( 3 ),
          left: LayoutScaler.widthFromDesign( 3 )
        }
      },
      {
        color: "rgba(0, 0, 0, 0.31)",
        sides: [ true, true, true, true ],
        blur: LayoutScaler.widthFromDesign( 3 )
      }
    );
    this.addChild( content_bg );

    this.avatars_scrollable_container = new UIScrollableContainerCSS(
      this.ctx,
      content_x,
      content_y + gap,
      content_w,
      content_h - gap * 2,
      {
        scroll_speed: 1.0,
        enable_momentum: true,
        enable_touch_scroll: true,
        scroll_sensitivity: 0.8,
        enable_rubber_band: true,
        rubber_band_resistance: 0.5,
        rubber_band_max_distance: 60,
        rubber_band_progression: 3.0
      }
    );

    const gap_y           = LayoutScaler.heightFromDesign( design_gap_y );
    const item_size       = LayoutScaler.widthFromDesign( design_item_size );
    const border_overhang = LayoutScaler.widthFromDesign( design_border_overhang );

    // Facebook support is intentionally kept behind a temporary feature flag.
    const slots: number[] = [];
    if ( AvatarSelectionDialog.FACEBOOK_AVATAR_ENABLED )
      slots.push( AvatarUtils.FACEBOOK_AVATAR_ID );

    for ( let i = 0; i < 20; i++ )
      slots.push( i );

    const total_rows             = Math.ceil( slots.length / this.COLS );
    const content_container_height = border_overhang + total_rows * item_size + ( total_rows - 1 ) * gap_y;

    this.avatars_scrollable_container.setContentHeight( content_container_height, item_size + gap_y, border_overhang );

    const container_w = this.avatars_scrollable_container.getRect().getW();
    const gap_x = ( container_w - this.COLS * item_size ) / ( this.COLS + 1 );
    const start_x = gap_x;

    for ( let i = 0; i < slots.length; i++ )
    {
      const avatar_id = slots[i];
      const row = Math.floor( i / this.COLS );
      const col = i % this.COLS;

      const x = start_x + col * ( item_size + gap_x );
      const y = border_overhang + row * ( item_size + gap_y );

      const is_facebook_slot = avatar_id === AvatarUtils.FACEBOOK_AVATAR_ID;

      const avatar_item = new UIAvatarSelectionItem(
        this.avatars_scrollable_container.getContentCtxAt( y ),
        x,
        y,
        item_size,
        item_size,
        avatar_id,
        () => {},
        LayoutScaler.widthFromDesign( design_border_w ),
        0,
        undefined,
        false,
        {
          type: "linear-gradient",
          from: "#F9F4C9",
          to: "#B5A084",
          direction: "vertical"
        },
        is_facebook_slot ? this.facebook_av_url : undefined
      );

      this.avatar_items.push( avatar_item );
      this.grid_slots.push( { avatar_id, x, y, size: item_size } );
    }

    this.addChild( this.avatars_scrollable_container, false );
    this.avatars_scrollable_container.updateDOMPosition();
    this.avatars_scrollable_container.getOverlayHost().style.zIndex = "121";
    this.avatars_scrollable_container.hideElement( 1 );
    this.selectAvatar( this.selected_avatar_id );
  }

  private renderGrid(): void
  {
    this.avatars_scrollable_container.clearContent();
    for ( const item of this.avatar_items )
    {
      item.invalidateSubtree();
      item.render();
    }
  }

  private onGridTap( avatar_id: number ): void
  {
    const is_facebook_slot = avatar_id === AvatarUtils.FACEBOOK_AVATAR_ID;
    if ( is_facebook_slot && !this.facebook_av_url )
    {
      // No Facebook photo yet → FB not connected: send the user to the connect dialog.
      DialogManager.routeToDialog( new SaveDialog() );
      return;
    }

    void audioController.playSound( AudioClips.UI_CLICK );
    this.selectAvatar( avatar_id );
  }

  public override onDialogOpened(): void
  {
    super.onDialogOpened();
    setTimeout( () =>
    {
      if ( !this.isClosing() )
        this.avatars_scrollable_container.showElement( 150 );
    }, 260 );
  }

  protected override onCloseAnimationStarted( to_scale: number, duration_ms: number ): void
  {
    // The grid is a separate DOM layer above the dialog canvas: shrink it with
    // the dialog instead of only fading it at full size.
    const host = this.avatars_scrollable_container.getOverlayHost();
    if ( !DialogManager.animateCloseDom( host, this, to_scale, duration_ms ) )
      this.avatars_scrollable_container.hideElement( duration_ms );
  }

  public override onDialogWillClose(): void
  {
    this.avatars_scrollable_container.destroy();
  }

  private selectAvatar( avatar_id: number ): void
  {
    this.selected_avatar_id = avatar_id;
    this.main_avatar.setAvatarId( avatar_id, this.facebook_av_url );

    if ( this.last_avatar_id !== avatar_id )
    {
      this.activeConfirmButton();
    }
    else
    {
      this.inactiveConfirmButton();
    }

    for ( const item of this.avatar_items )
    {
      item.setSelected( item.avatar_id === avatar_id );
    }

    this.renderGrid();
  }

  protected updateBasic( now: number ): void
  {
    super.updateBasic( now );
    if ( this.avatars_scrollable_container )
    {
      this.avatars_scrollable_container.update( now );
    }
  }

  public onTouchStart( p: any, now: number ): boolean
  {
    this.grid_touch_down = { x: p.x, y: p.y };
    return super.onTouchStart( p, now );
  }

  public onTouchMove( p: any, now: number ): boolean
  {
    super.onTouchMove( p, now );
    return false;
  }

  public onTouchEnd( p: any, now: number ): void
  {
    super.onTouchEnd( p, now );

    const down = this.grid_touch_down;
    this.grid_touch_down = null;
    if ( !down )
      return;

    const moved = Math.abs( p.x - down.x ) + Math.abs( p.y - down.y );
    if ( moved > 8 )
      return;

    const container = this.avatars_scrollable_container;
    const container_pos = container.getAbsolutePosition();
    const local_x = p.x - container_pos.x;
    const local_y = p.y - container_pos.y;

    if ( local_x < 0 || local_y < 0
      || local_x > container.getRect().getW()
      || local_y > container.getRect().getH() )
      return;

    const content_y = local_y + container.getScrollOffset();
    for ( const slot of this.grid_slots )
    {
      if ( local_x >= slot.x && local_x <= slot.x + slot.size
        && content_y >= slot.y && content_y <= slot.y + slot.size )
      {
        this.onGridTap( slot.avatar_id );
        return;
      }
    }
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public getSelectedAvatarId(): number
  {
    return this.selected_avatar_id;
  }

  private drawBannerArea(): void
  {
    const design_banner_w      = 850;
    const design_banner_h      = 1295.6;
    const banner_w = LayoutScaler.widthFromDesign( design_banner_w );
    const banner_h = LayoutScaler.heightFromDesign( design_banner_h );
    const banner_x = ( this.rect.getW() - banner_w ) / 2;
    const banner_y = ( this.rect.getH() - banner_h ) / 2;

    const banner = new UINineSliceElement(
      this.ctx,
      banner_x,
      banner_y,
      banner_w,
      banner_h,
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );

    this.addChild( banner );
  }

  private inactiveConfirmButton(): void
  {
    if ( !this.confirm_button )
      return;

    this.confirm_button.setClickable( false );
    this.confirm_button.setImageId( ImgID.UI_AvatarSelectInactiveBtn );
    setButtonTextOutline( [ this.confirm_text ], false, ColorScheme.more_lives_buy_outline );
    this.invalidateSubtree();
  }

  private activeConfirmButton(): void
  {
    if ( !this.confirm_button )
      return;

    this.confirm_button.setClickable( true );
    this.confirm_button.setImageId( ImgID.UI_AvatarSelectActiveBtn );
    setButtonTextOutline( [ this.confirm_text ], true, ColorScheme.more_lives_buy_outline );
    this.invalidateSubtree();
  }

  private createConfirmButton(): void
  {
    const design_button_w     = 512;
    const design_button_h     = 169;
    const design_button_pad_y = 45;
    const design_text_size    = 54;

    const button_w = LayoutScaler.widthFromDesign( design_button_w );
    const button_h = LayoutScaler.heightFromDesign( design_button_h );
    const button_y = this.rect.getH() - button_h - LayoutScaler.heightFromDesign( design_button_pad_y );
    const button_x = ( this.rect.getW() - button_w ) / 2;
    this.confirm_button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_AvatarSelectInactiveBtn,
      {},
      true
    );
    this.confirm_button.setClickable( false );

    this.confirm_button.setOnClick( () =>
    {
      localStorage.setItem( AvatarSelectionDialog.AVATAR_KEY, String( this.selected_avatar_id ) );
      this.onConfirm( this.selected_avatar_id );
      updatePlayer( { avatar_id: this.selected_avatar_id } ).catch( e => console.warn( '[Avatar] sync failed', e ) );
      const stats = StatisticsStorage.load();
      const name = localStorage.getItem( 'player_nickname' ) ?? 'Player';
      void LeaderboardSyncService.syncProfile( stats.level, name, this.selected_avatar_id, this.facebook_av_url ?? undefined );
      this.close();
    } );

    const confirm_text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        localization_key: "CONFIRM",
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.geologica,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    this.confirm_button.addChild( confirm_text, true );
    this.confirm_text = confirm_text;
    setButtonTextOutline( [ confirm_text ], false, ColorScheme.more_lives_buy_outline );
    this.addChild( this.confirm_button );
  }
}
